import { CreateNotePayload, Note, UpdateCrmContactNotePayload } from "types/crm/notes";
import { usePaginatedList } from "../usePaginatedList";
import { contactApi, contactQueryKeys } from "service/crm/api/contact";
import { useEntityCreateMutation } from "../useEntityMutation";
import { useMutation, useQueryClient } from "@tanstack/react-query";


export const useContactNotes = (contactUuid: string, pageSize = 20) => {
  const { items, ...rest } = usePaginatedList<Note>(
    contactQueryKeys.notes(contactUuid),
    (page) => contactApi.getContactNotes(contactUuid, page, pageSize),
    !!contactUuid,
  );
  return { notes: items, ...rest };
};

export const useCreateNoteMutation = () =>
  useEntityCreateMutation<CreateNotePayload, Note>(
    (payload) => contactApi.addContactNote(payload),
    (variables) => [
      contactQueryKeys.notes(variables.contactUuid),
      contactQueryKeys.activities(variables.contactUuid),
    ],
  );

export const useUpdateContactNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmContactNotePayload) =>
      contactApi.updateContactNote(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this contact's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...contactQueryKeys.all, "notes", variables.contactUuid],
      });
      queryClient.invalidateQueries({
        queryKey: contactQueryKeys.activities(variables.contactUuid),
      });
    },
  });
};

export const useDeleteContactNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contactUuid,
      noteUuid,
    }: {
      contactUuid: string;
      noteUuid: string;
    }) => contactApi.deleteContactNote(contactUuid, noteUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...contactQueryKeys.all, "notes", variables.contactUuid],
      });
      queryClient.invalidateQueries({
        queryKey: contactQueryKeys.activities(variables.contactUuid),
      });
    },
  });
};